import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import ProductCard from "../product/ProductCard";
import SellerSearch from "./SellerSearch";
import SkeletonGrid from "../common/SkeletonGrid";
import { API_BASE } from "../../api/api";
import { useSEO } from "../../utils/seo";
import { trackSearch } from "../../utils/analytics";
import { getVisitorId } from "../../utils/consent";
import { useApiDown } from "../../utils/apiStatus";
import "./SearchPage.css";

const FILTER_HEADINGS = {
  // "Hot Deal" is the underlying tag value (the "flash_sale" rail's
  // eligibility rule and the navbar's "Flash Deals" link both point here)
  // — show it as
  // Flash Deals/Sale so the destination reads consistently with the link
  // that brought the shopper here, not the raw tag string.
  tag:         (tag) => tag === "Hot Deal"
    ? { eyebrow: "Limited Time", title: "Flash Deals" }
    : { eyebrow: "Collection",   title: tag },
  trending:    () =>    ({ eyebrow: "Hot Right Now",   title: "Trending Now" }),
  lowStock:    () =>    ({ eyebrow: "Hurry",           title: "Almost Gone" }),
  backInStock: () =>    ({ eyebrow: "Just Restocked",  title: "Back in Stock" }),
  preorder:    () =>    ({ eyebrow: "Reserve Yours",   title: "Pre-Order" }),
};

// While the API is down (a release holding it), an empty result is not
// "no products": say search is briefly unavailable instead.
const UNAVAILABLE = "Search is unavailable for a few minutes while we make an update.";

const SearchPage = ({ onAddToCart }) => {
  const apiDown = useApiDown();
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const tag = params.get("tag") || "";
  const trending = params.get("trending") === "1";
  const lowStock = params.get("lowStock") === "1";
  const backInStock = params.get("backInStock") === "1";
  const preorder = params.get("preorder") === "1";
  // Products | Sellers (shop pages, 2026-10-17). The tabs show on a text
  // search and on the Sellers tab; filter views (tags, trending...) are
  // product lists only.
  const sellersTab = params.get("tab") === "sellers";

  const filterKey = tag ? "tag" : trending ? "trending" : lowStock ? "lowStock" : backInStock ? "backInStock" : preorder ? "preorder" : null;
  const filterHeading = filterKey ? FILTER_HEADINGS[filterKey](tag) : null;

  useSEO({
    title: q ? `Search: "${q}"` : filterHeading ? filterHeading.title : "Search",
    description: q
      ? `Search results for "${q}" on Emporia, a Kenyan online marketplace with M-Pesa payments and nationwide delivery.`
      : filterHeading
        ? `Browse ${filterHeading.title} on Emporia, a Kenyan online marketplace with M-Pesa payments and nationwide delivery.`
        : "Search fashion, electronics, home, beauty and more on Emporia, a Kenyan online marketplace.",
    path: q ? `/search?q=${encodeURIComponent(q)}` : filterKey ? `/search?${params.toString()}` : "/search",
  });

  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(false);
  // Opaque per-search-instance token (Emporia Brain P1.2) — lets a later
  // click on one of these results be attributed back to this exact search
  // without ever carrying the raw query string in click metadata. Only
  // meaningful for real text search, so it's only (re)generated in that
  // branch below; filter/tag/trending views leave it null.
  const [searchRequestId, setSearchRequestId] = useState(null);

  useEffect(() => {
    if (sellersTab) return;
    let url;
    if (q.trim()) {
      // visitor_id (Emporia Brain P2.3) is purely additive — the backend
      // only ever acts on it if a "brain_search_ranking" flag is active,
      // which it isn't by default, so this changes nothing visible yet.
      const vid = getVisitorId();
      url = `${API_BASE}/products?q=${encodeURIComponent(q)}${vid ? `&visitor_id=${encodeURIComponent(vid)}` : ""}`;
    } else if (tag) {
      url = `${API_BASE}/products?tag=${encodeURIComponent(tag)}`;
    } else if (trending) {
      url = `${API_BASE}/products/trending?limit=24`;
    } else if (lowStock) {
      url = `${API_BASE}/products?max_stock=5`;
    } else if (backInStock) {
      url = `${API_BASE}/products?restocked_within=14`;
    } else if (preorder) {
      url = `${API_BASE}/products?is_preorder=true`;
    } else {
      setProducts([]);
      return;
    }

    // A new search cancels the one still in flight, so its late answer can
    // never overwrite this one's results (speed brief item 4).
    const controller = new AbortController();
    setLoading(true);
    fetch(url, { signal: controller.signal })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((data) => {
        setProducts(data);
        setLoading(false);
        if (q.trim()) {
          const requestId = (window.crypto && window.crypto.randomUUID)
            ? window.crypto.randomUUID()
            : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
          setSearchRequestId(requestId);
          trackSearch(q, data.length, requestId);
        } else {
          setSearchRequestId(null);
        }
      })
      .catch(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [q, tag, trending, lowStock, backInStock, preorder, sellersTab]);

  const hasFilter = Boolean(q || filterKey);
  const showTabs = sellersTab || (q.trim() && !filterKey);
  const tabHref = (tab) => {
    const next = new URLSearchParams();
    if (q.trim()) next.set("q", q.trim());
    if (tab === "sellers") next.set("tab", "sellers");
    const qs = next.toString();
    return `/search${qs ? `?${qs}` : ""}`;
  };
  const tabs = showTabs && (
    <nav className="search-tabs" aria-label="Search for">
      <Link to={tabHref("products")} className={`search-tab${sellersTab ? "" : " active"}`} aria-current={sellersTab ? undefined : "page"}>Products</Link>
      <Link to={tabHref("sellers")} className={`search-tab${sellersTab ? " active" : ""}`} aria-current={sellersTab ? "page" : undefined}>Sellers</Link>
    </nav>
  );

  if (sellersTab) {
    return (
      <main className="search-page">
        <div className="search-header">
          <p className="search-eyebrow">Shops on Emporia</p>
          <h1>Find a shop</h1>
        </div>
        {tabs}
        <SellerSearch />
      </main>
    );
  }

  return (
    <main className="search-page">
      {tabs}
      <div className="search-header">
        {q ? (
          <>
            <p className="search-eyebrow">Search results</p>
            <h1>"{q}"</h1>
            {!loading && (
              <p className="search-count">
                {products.length === 0
                  ? (apiDown ? UNAVAILABLE : "No products found")
                  : `${products.length} product${products.length !== 1 ? "s" : ""} found`}
              </p>
            )}
          </>
        ) : filterHeading ? (
          <>
            <p className="search-eyebrow">{filterHeading.eyebrow}</p>
            <h1>{filterHeading.title}</h1>
            {!loading && (
              <p className="search-count">
                {products.length === 0
                  ? (apiDown ? UNAVAILABLE : "No products found")
                  : `${products.length} product${products.length !== 1 ? "s" : ""} found`}
              </p>
            )}
          </>
        ) : (
          <>
            <h1>Search</h1>
            <p className="search-count">Type a query in the search bar above to find products.</p>
          </>
        )}
      </div>

      {loading ? (
        <SkeletonGrid count={4} className="search-grid" />
      ) : products.length > 0 ? (
        <div className="search-grid">
          {products.map((p, i) => (
            <div key={p.id} className="product-card-anim" style={{ "--i": i }}>
              <ProductCard
                product={p}
                onAddToCart={onAddToCart}
                searchRequestId={q.trim() ? searchRequestId : null}
                searchPosition={i}
                searchResultCount={products.length}
              />
            </div>
          ))}
        </div>
      ) : hasFilter && !loading ? (
        <div className="search-empty">
          {apiDown ? (
            <p>{UNAVAILABLE}</p>
          ) : q ? (
            <>
              <p>No products match <strong>"{q}"</strong>.</p>
              <p>Try a different term, or browse our <Link to="/">homepage</Link>.</p>
            </>
          ) : (
            <>
              <p>No products currently match this filter.</p>
              <p>Browse our <Link to="/">homepage</Link> instead.</p>
            </>
          )}
        </div>
      ) : null}
    </main>
  );
};

export default SearchPage;
